import { ARCADE_FPS, progress1000 } from './common.js';
import type { ArcadeBaseState, ArcadeSimulation } from './common.js';
import { ArcadeRng } from './rng.js';

/**
 * MRT Dash: an endless run through the station and out into the heartland.
 * Jump over luggage and cones, duck under low signboards, grab EZ-link cards.
 * Three hearts; the pace picks up the longer you last.
 */

export const MD_WIDTH = 160;
export const MD_HEIGHT = 120;
export const MD_GROUND = 100;
export const MD_RUNNER_X = 26;
export const MD_RUNNER_W = 10;
export const MD_STAND_H = 18;
export const MD_DUCK_H = 10;
export const MD_FRAMES = 90 * ARCADE_FPS;
export const MD_HEARTS = 3;
/** Signboards hang with their bottom edge here: a standing runner hits it, a ducking one clears it. */
export const MD_SIGN_BOTTOM = 87;

export const MD_JUMP = 1;
export const MD_DUCK_DOWN = 2;
export const MD_DUCK_UP = 3;

const GRAVITY = 85;
const JUMP_VELOCITY = -1350;

export type MdObstacleType = 'luggage' | 'cone' | 'sign';

export interface MdObstacle {
  type: MdObstacleType;
  /** Fixed point, 1/256 px. */
  x: number;
  w: number;
  top: number;
  bottom: number;
  hit: boolean;
}

export interface MdCoin {
  x: number;
  y: number;
}

export interface MrtDashState extends ArcadeBaseState {
  rng: ArcadeRng;
  /** Feet position, fixed point. */
  y: number;
  vy: number;
  onGround: boolean;
  ducking: boolean;
  duckHeld: boolean;
  /** Pixels per frame, fixed point. */
  speed: number;
  distance: number;
  nextSpawnAt: number;
  obstacles: MdObstacle[];
  coins: MdCoin[];
  hearts: number;
  invulnerable: number;
  coinsCollected: number;
}

const OBSTACLE_SHAPES: Record<MdObstacleType, { w: number; top: number; bottom: number }> = {
  luggage: { w: 12, top: MD_GROUND - 11, bottom: MD_GROUND },
  cone: { w: 8, top: MD_GROUND - 12, bottom: MD_GROUND },
  sign: { w: 18, top: 58, bottom: MD_SIGN_BOTTOM },
};

export const mrtDash: ArcadeSimulation<MrtDashState> = {
  maxFrames: MD_FRAMES,

  create(seed) {
    return {
      frame: 0,
      over: false,
      score: 0,
      fx: [],
      rng: new ArcadeRng(seed),
      y: MD_GROUND * 256,
      vy: 0,
      onGround: true,
      ducking: false,
      duckHeld: false,
      speed: 512,
      distance: 0,
      nextSpawnAt: 120 * 256,
      obstacles: [],
      coins: [],
      hearts: MD_HEARTS,
      invulnerable: 0,
      coinsCollected: 0,
    };
  },

  validCode(code) {
    return code === MD_JUMP || code === MD_DUCK_DOWN || code === MD_DUCK_UP;
  },

  step(s, codes) {
    if (s.over) return;
    s.fx = [];
    for (const code of codes) {
      if (code === MD_JUMP && s.onGround) {
        s.vy = JUMP_VELOCITY;
        s.onGround = false;
        s.fx.push({ kind: 'jump', x: MD_RUNNER_X, y: MD_GROUND });
      } else if (code === MD_DUCK_DOWN) {
        s.duckHeld = true;
        // Ducking mid-air slams back down.
        if (!s.onGround) s.vy = Math.max(s.vy, 700);
      } else if (code === MD_DUCK_UP) {
        s.duckHeld = false;
      }
    }

    // Vertical motion.
    if (!s.onGround) {
      s.vy += GRAVITY;
      s.y += s.vy;
      if (s.y >= MD_GROUND * 256) {
        s.y = MD_GROUND * 256;
        s.vy = 0;
        s.onGround = true;
      }
    }
    s.ducking = s.duckHeld && s.onGround;

    // The world scrolls.
    const p = progress1000(s.frame, MD_FRAMES);
    s.speed = 512 + Math.floor((p * 768) / 1000);
    s.distance += s.speed;
    for (const obstacle of s.obstacles) obstacle.x -= s.speed;
    for (const coin of s.coins) coin.x -= s.speed;
    s.obstacles = s.obstacles.filter((o) => o.x + o.w * 256 > 0);
    s.coins = s.coins.filter((c) => c.x + 8 * 256 > 0);

    if (s.distance >= s.nextSpawnAt) spawn(s);

    // Collisions against the runner's box.
    const height = s.ducking ? MD_DUCK_H : MD_STAND_H;
    const feet = s.y >> 8;
    const top = feet - height;
    if (s.invulnerable > 0) s.invulnerable -= 1;
    for (const obstacle of s.obstacles) {
      if (obstacle.hit) continue;
      const ox = obstacle.x >> 8;
      const overlapX = ox < MD_RUNNER_X + MD_RUNNER_W && ox + obstacle.w > MD_RUNNER_X;
      const overlapY = obstacle.top < feet && obstacle.bottom > top;
      if (overlapX && overlapY && s.invulnerable === 0) {
        obstacle.hit = true;
        s.hearts -= 1;
        s.invulnerable = 90;
        s.fx.push({ kind: 'hurt', x: MD_RUNNER_X, y: top });
      }
    }
    s.coins = s.coins.filter((coin) => {
      const cx = coin.x >> 8;
      const hit = cx < MD_RUNNER_X + MD_RUNNER_W && cx + 8 > MD_RUNNER_X && coin.y < feet && coin.y + 8 > top;
      if (hit) {
        s.coinsCollected += 1;
        s.fx.push({ kind: 'coin', x: cx, y: coin.y, value: 10 });
      }
      return !hit;
    });

    s.score = Math.floor(s.distance / 256 / 8) + s.coinsCollected * 10;
    s.frame += 1;
    if (s.hearts <= 0 || s.frame >= MD_FRAMES) s.over = true;
  },
};

function spawn(s: MrtDashState): void {
  const roll = s.rng.int(0, 100);
  const type: MdObstacleType = roll < 40 ? 'luggage' : roll < 70 || s.frame < 300 ? 'cone' : 'sign';
  const shape = OBSTACLE_SHAPES[type];
  s.obstacles.push({ type, x: MD_WIDTH * 256, w: shape.w, top: shape.top, bottom: shape.bottom, hit: false });

  // Gaps widen a little with speed so every obstacle stays clearable.
  const gap = s.rng.int(80, 150) + Math.floor(s.speed / 8);
  if (s.rng.int(0, 100) < 55) {
    // A card halfway to the next obstacle: low ones on the floor, high ones need a jump.
    const high = s.rng.int(0, 2) === 1;
    s.coins.push({ x: (MD_WIDTH + Math.floor(gap / 2)) * 256, y: high ? 62 : MD_GROUND - 10 });
  }
  s.nextSpawnAt = s.distance + gap * 256;
}
